import { useState, type FormEvent } from 'react'
import type { PmAgent, Project, Runner, RunnerAgent } from '../api/types'
import { activeRunners, agentName } from '../code/format'
import { needsServer } from '../components/needsServer'
import { useMounted } from '../components/layout'
import { toast } from '../components/toasts'
import { useUnsavedChanges } from '../components/unsaved'
import { useRunners } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { useSettingsProject } from './selection'
import { settingsWords } from './words'
import { ReleaseSettings } from './ReleaseSettings'
import { WorkerLimitsSettings } from './WorkerLimitsSettings'

const words = settingsWords.pm

export function AdvancedSettings() {
  const project = useSettingsProject()
  return project ? <div key={project.id} className="settings-advanced"><PmAgentForm project={project} /><WorkerLimitsSettings project={project} /><details className="settings-section settings-advanced-group"><summary>{settingsWords.releases.title}</summary><ReleaseSettings project={project} /></details></div> : null
}

const same = (a: PmAgent, b: PmAgent) => a.agent === b.agent && a.model === b.model && a.runner_id === b.runner_id
const canRun = (agent: RunnerAgent) => agent.signed_in !== false

/**
 * The agent a project's PM agent runs as (Poulpe-406), chosen as in New
 * session: a runner or any runner of the project, an agent it offers and a
 * model. What is saved stays a choice while no runner
 * offers it, so the pane shows what the server holds; whether a runner offers
 * it is asked when a batch starts its PM agent.
 */
function PmAgentForm({ project }: { project: Project }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const { runners } = useRunners()
  // Cached projects from before this setting existed have no PM agent field.
  const saved = project.pm_agent ?? { agent: 'claude', model: null, runner_id: null }
  const [edit, setEdit] = useState<PmAgent | null>(null)
  const pm = edit ?? saved
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const dirty = edit !== null && !same(edit, saved)
  useUnsavedChanges(dirty)

  const listed = activeRunners(runners)
  const runner = pm.runner_id ? listed.find(r => r.id === pm.runner_id) : undefined
  // A runner named offers its own agents; any runner, every agent that one which takes work from elsewhere offers.
  const offers: RunnerAgent[] = runner ? runner.agents : listed.filter(r => r.accepts_work_from_elsewhere).flatMap(r => r.agents)
  const agents = unique([...offers.map(offer => offer.agent), pm.agent])
  const signedIn = (agent: string) => !runner || runner.agents.some(offer => offer.agent === agent && canRun(offer))
  // Only the models the start path admits (`models`); a saved one no runner admits now still shows.
  const admitted = unique(offers.filter(offer => offer.agent === pm.agent).flatMap(offer => offer.models))
  const models = unique([...admitted, ...(pm.model ? [pm.model] : [])])
  const noModels = models.length === 0
  // A model is chosen explicitly whenever the agent admits any (never the agent's default).
  const needsModel = !noModels && !pm.model
  const change = (next: PmAgent) => { setEdit(next); setProblem(null) }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (store.getState().offline || busy || !dirty || needsModel) return
    setBusy(true); setProblem(null)
    const outcome = await store.setPmAgent(project.id, pm)
    if (!mounted.current) return
    setBusy(false)
    if (outcome.ok) { setEdit(null); toast(settingsWords.saved) }
    else setProblem(outcome.message)
  }
  return <section className="settings-section">
    <h2 className="settings-title">{words.title}</h2>
    <p className="muted">{words.about}</p>
    <form className="setup-form" aria-label={words.title} onSubmit={event => void save(event)}>
      <label className="field"><span>{words.runner}</span>
        <select value={pm.runner_id ?? ''} disabled={busy} onChange={event => change({ ...pm, runner_id: event.target.value || null })}>
          <option value="">{words.anyRunner}</option>
          {listed.map(r => <option key={r.id} value={r.id} disabled={!r.accepts_work_from_elsewhere}>{runnerLabel(r)}</option>)}
          {pm.runner_id && !runner && <option value={pm.runner_id}>{words.gone}</option>}
        </select>
      </label>
      <label className="field"><span>{words.agent}</span>
        <select value={pm.agent} disabled={busy} onChange={event => change({ ...pm, agent: event.target.value, model: null })}>
          {agents.map(agent => <option key={agent} value={agent} disabled={agent !== pm.agent && !signedIn(agent)}>
            {signedIn(agent) ? agentName(agent) : `${agentName(agent)} · ${words.notSignedIn}`}
          </option>)}
        </select>
      </label>
      <label className="field"><span>{words.model}</span>
        <select value={pm.model ?? ''} disabled={busy || noModels} onChange={event => change({ ...pm, model: event.target.value || null })}>
          {pm.model === null && <option value="" disabled>{words.chooseModel}</option>}
          {models.map(model => <option key={model} value={model}>{model}</option>)}
        </select>
      </label>
      {noModels && <p className="muted">{words.noModels}</p>}
      {problem && <p role="alert" className="text-danger">{problem}</p>}
      <div className="form-actions"><button className="button" disabled={offline || busy || !dirty || needsModel} {...needsServer(offline)}>{busy ? words.saving : words.save}</button></div>
    </form>
  </section>
}

function runnerLabel(runner: Runner): string {
  return [runner.name, !runner.accepts_work_from_elsewhere && words.elsewhere].filter(Boolean).join(' · ')
}

function unique(names: readonly string[]): string[] {
  return [...new Set(names)]
}
